import React, { useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import { useDispatch } from "react-redux";
import SlideDrawer from "../common/SlideDrawer";
import UnderlineInput from "../common/UnderlineInput";
import { searchPurchaseOrders } from "redux/erp/paymentRequestSlice";

const money = (v) => (Number(v) || 0).toLocaleString("en-US");

// Cảnh báo, không phải chặn: kế toán vẫn có quyền link PO chưa nhập kho đủ, nhưng
// phải thấy rõ mình đang làm gì.
const RECEIPT = {
  fully_received:     { label: "Fully received", tone: "tw-bg-[#edfaef] tw-text-geist-success" },
  partially_received: { label: "Partially received", tone: "tw-bg-[#fff8ee] tw-text-[#a05e03]" },
  not_received:       { label: "Not received", tone: "tw-bg-[#fff8ee] tw-text-[#a05e03]" },
};

/**
 * Chọn Purchase Order bên AX để gắn vào phiếu.
 *
 * Props:
 *   show, onClose
 *   onPick(poNum)  — gọi khi người dùng chọn một PO
 *   busy           — đang gửi request link
 */
const PurchaseOrderDrawer = ({ show, onClose, onPick, busy }) => {
  const dispatch = useDispatch();
  const [q, setQ] = useState("");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searched, setSearched] = useState(false);
  const timer = useRef();

  useEffect(() => {
    if (!show) return undefined;
    clearTimeout(timer.current);
    timer.current = setTimeout(async () => {
      setLoading(true);
      try {
        const res = await dispatch(searchPurchaseOrders({ q })).unwrap();
        setRows(res?.data || []);
      } catch {
        setRows([]);
      } finally {
        setLoading(false);
        setSearched(true);
      }
    }, 400);
    return () => clearTimeout(timer.current);
  }, [q, show, dispatch]);

  return (
    <SlideDrawer
      show={show}
      onHide={onClose}
      width="46%"
      minWidth="480px"
      title={
        <div>
          <div className="tw-text-sm tw-font-semibold tw-text-geist-fg">Link a purchase order</div>
          <div className="tw-text-[12px] tw-text-geist-fg-3">
            The journal is built from this PO — vendor, expense account, tax and the three dimensions.
          </div>
        </div>
      }
    >
      <div className="tw-p-5">
        <UnderlineInput
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search by PO number, vendor or invoice…"
        />

        <div className="tw-mt-4 tw-divide-y tw-divide-geist-border">
          {loading && <div className="tw-py-8 tw-text-center tw-text-[13px] tw-text-geist-fg-3">Searching…</div>}

          {!loading && searched && rows.length === 0 && (
            <div className="tw-py-8 tw-text-center tw-text-[13px] tw-text-geist-fg-3">
              No purchase order found.
            </div>
          )}

          {!loading &&
            rows.map((po) => {
              const receipt = RECEIPT[po.receipt_status] || RECEIPT.not_received;
              return (
                <div
                  key={po.code}
                  role="button"
                  tabIndex={0}
                  onClick={() => !busy && onPick(po.code)}
                  onKeyDown={(e) => e.key === "Enter" && !busy && onPick(po.code)}
                  className={`tw-py-3 tw-px-1 tw-flex tw-items-start tw-gap-3 ${
                    busy ? "tw-opacity-60 tw-cursor-default" : "tw-cursor-pointer hover:tw-bg-geist-100"
                  }`}
                >
                  <div className="tw-flex tw-h-8 tw-w-8 tw-flex-shrink-0 tw-items-center tw-justify-center tw-rounded-geist-sm tw-bg-[#eef4ff] tw-text-geist-accent">
                    <i className="bi bi-receipt-cutoff" />
                  </div>

                  <div className="tw-min-w-0 tw-flex-1">
                    <div className="tw-flex tw-items-baseline tw-gap-2">
                      <span className="tw-text-[13px] tw-font-semibold tw-text-geist-fg">{po.code}</span>
                      <span className="tw-flex-1 tw-truncate tw-text-[13px] tw-text-geist-fg-2">
                        {po.vendor_name}
                      </span>
                      {/* PO của AX chỉ mang số NET (netAmountTotal), không có tổng gồm
                          thuế. Ghi rõ "net" để người duyệt không đối chiếu nhầm với
                          tổng tiền trên phiếu. */}
                      <span className="tw-text-[13px] tw-font-semibold tw-tabular-nums tw-text-geist-fg">
                        {money(po.total_net)} {po.currency}
                        <span className="tw-ml-1 tw-font-normal tw-text-geist-fg-3">net</span>
                      </span>
                    </div>
                    <div className="tw-mt-1 tw-flex tw-items-center tw-gap-2 tw-text-[12px] tw-text-geist-fg-3">
                      <span
                        className={`tw-inline-flex tw-px-1.5 tw-py-0.5 tw-rounded tw-font-bold ${receipt.tone}`}
                      >
                        {receipt.label}
                      </span>
                      {po.invoice_ref && <span>#{po.invoice_ref}</span>}
                      {po.po_date && <span>· {po.po_date}</span>}
                    </div>
                  </div>
                </div>
              );
            })}
        </div>
      </div>
    </SlideDrawer>
  );
};

PurchaseOrderDrawer.propTypes = {
  show: PropTypes.bool,
  onClose: PropTypes.func.isRequired,
  onPick: PropTypes.func.isRequired,
  busy: PropTypes.bool,
};

export default PurchaseOrderDrawer;
